/**
 * Role Guard Component
 * Protects routes based on user roles
 */

import { Navigate } from 'react-router-dom';

import { useAuthStore } from '../stores';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: string[];
  fallback?: React.ReactNode;
  redirectTo?: string;
}

/**
 * Check if user has any of the allowed roles
 */
export function hasRole(userRoles: string[] | undefined, allowedRoles: string[]): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  return allowedRoles.some((role) => userRoles.includes(role));
}

/**
 * Check if user is an admin or supervisor role
 */
export function isAdminOrSupervisor(userRoles: string[] | undefined): boolean {
  return hasRole(userRoles, ['ADMIN', 'SUPER_ADMIN', 'DISPATCHER']);
}

/**
 * RoleGuard component - wraps routes to protect by role
 */
export function RoleGuard({ children, allowedRoles, fallback, redirectTo = '/' }: RoleGuardProps) {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const userRoles = user.roles as string[] | undefined;

  if (!hasRole(userRoles, allowedRoles)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * Hook to check user roles
 */
export function useUserRoles() {
  const { user, getActiveRole } = useAuthStore();
  const userRoles = (user?.roles as string[]) || [];
  const activeRole = getActiveRole() as string | null;

  return {
    roles: userRoles,
    activeRole,
    hasRole: (roles: string[]) => hasRole(userRoles, roles),
    isActiveRole: (roles: string[]) => (activeRole ? roles.includes(activeRole) : false),
    isAdmin: hasRole(userRoles, ['ADMIN', 'SUPER_ADMIN']),
    isAdminOrSupervisor: isAdminOrSupervisor(userRoles),
    isDispatcher: hasRole(userRoles, ['DISPATCHER']),
    isYardWorker: hasRole(userRoles, ['YARD_WORKER']),
    isCleaning: hasRole(userRoles, ['CLEANING']),
    isWorkshop: hasRole(userRoles, ['WORKSHOP']),
    isQCInspector: hasRole(userRoles, ['QC_INSPECTOR']),
  };
}
